/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'

// eslint-disable-next-line import/no-extraneous-dependencies
import { createUseStyles } from 'react-jss'
import moment from 'moment'
import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'
import { zIndexes } from '../../../theme/zIndex'
import { Close as CloseIcon, Tips as TipsIcon } from '../../../assets/icons'
import { baseButton } from '../../../theme/reuseables'
import { L_HTML } from '../../providers/Lokalise'

const useStyles = createUseStyles(() => ({
    '@keyframes slideRight': {
        from: {
            transform: 'translateY(20px)',
            opacity: 0,
        },
        to: {
            transform: 'translateY(0)',
            opacity: 1,
        },
    },
    containerBG: {
        position: 'fixed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        left: '0',
        top: '0',
        height: '100dvh',
        width: '100%',
        zIndex: zIndexes.drawer,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        padding: '1rem',
        visibility: 'hidden',
        '&.active': {
            visibility: 'visible',
        },
    },
    container: {
        display: 'flex',
        borderRadius: '10px',
        flexDirection: 'column',
        width: '1200px',
        maxWidth: '100%',
        minHeight: '65vh',
        maxHeight: '100%',
        animation: `.3s $slideRight forwards`,
        visibility: 'visible',
        overflow: 'auto',
        right: 0,
        boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.25)',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                flexDirection: 'row',
            },
        },
    },
    left: {
        backgroundColor: colors.white,
        padding: '2rem',
        paddingTop: '2.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                paddingRight: '5rem',
            },
        },
    },
    right: {
        backgroundColor: colors.gray,
        color: colors.gray900,
        padding: '2rem',
        paddingTop: '3rem',
        flexGrow: 1,
    },
    headline: {
        fontSize: '2rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                fontSize: '2.5rem',
            },
        },
    },
    headlines: {
        fontWeight: 'normal',
        marginBottom: '2rem',
    },
    leftText: {
        fontWeight: 'normal',
        marginBottom: '1rem',
    },
    list: {
        position: 'relative',
    },
    listItem: {
        position: 'relative',
        listStyle: 'none',
        marginBottom: '2rem',
    },

    listItemBold: {
        fontWeight: 'bold',
        marginBottom: '0.5rem',
    },
    listItemSpan: {
        position: 'absolute',
        left: '-28px',
    },
    video: {
        [`@media (max-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                maxWidth: '100%',
                height: 'auto',
                aspectRatio: '16 / 9',
            },
        },
    },
    button: {
        position: 'absolute',
        zIndex: zIndexes.drawer + 1,
        right: 0,
        top: 0,
        display: 'inline-block',
        margin: '10px 10px 0',
        borderRadius: '6px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        background: 'transparent',
        boxShadow: 0,
        padding: 0,
        height: '40px',
        width: '40px',
        '&:hover': {
            cursor: 'pointer',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        border: '0',
        '&:hover svg': {
            fill: colors.gray500,
        },
    },
    primaryButton: {
        ...baseButton,
        backgroundColor: colors.green500,
        color: colors.white,
        marginLeft: 'auto',
        marginTop: '5rem',
        '&:hover': {
            backgroundColor: colors.green600,
        },
    },
}))

const FirstLoginInfo: React.FC<{
    showVideo: boolean
    setClose: () => void
}> = ({ showVideo, setClose }) => {
    const node = React.useRef<HTMLDivElement>(null)
    const classes = useStyles()

    const [isShowing, setIsShowing] = React.useState(false)

    useEffect(() => {
        const loginData = localStorage.getItem('Login_data')
        const today = moment()
        if (loginData) {
            const loginJson = JSON.parse(loginData)
            const isMoreThanSixMonths = today.diff(loginJson.date, 'months') > 6

            if (!loginJson.agreed || isMoreThanSixMonths) {
                setIsShowing(true)
            }
        } else {
            const data = {
                date: today.format('YYYYMMDD'),
                agreed: false,
            }
            localStorage.setItem('Login_data', JSON.stringify(data))
            setIsShowing(true)
        }
    }, [])

    useEffect(() => {
        if (showVideo) {
            setIsShowing(true)
        }
    }, [showVideo])

    const closeInfo = (): void => {
        const today = moment()
        const data = {
            date: today.format('YYYYMMDD'),
            agreed: true,
        }
        localStorage.setItem('Login_data', JSON.stringify(data))
        setIsShowing(false)
    }

    React.useEffect(() => {
        if (isShowing) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
    }, [isShowing])

    if (!isShowing) {
        return null
    }
    return ReactDOM.createPortal(
        <div
            // prettier-ignore
            className={`${classes.containerBG} ${isShowing ? 'active' : 'hidden'}`}
            style={{
                display: isShowing ? 'flex' : 'none',
            }}
        >
            <div className={`${classes.container} ${'active'}`} ref={node}>
                <button
                    className={classes.button}
                    type="button"
                    onClick={() => {
                        setIsShowing(false)
                        setClose()
                    }}
                >
                    <CloseIcon width="38px" color={colors.green500} />
                </button>
                <div className={`${classes.left}`}>
                    <h2 className={`${classes.headline} ${classes.headlines}`}>
                        {L_HTML('first_login_welcome_text')}
                    </h2>
                    <p className={`${classes.leftText}`}>
                        {L_HTML('first_login_video_text')}
                    </p>
                    <iframe
                        className={`${classes.video}`}
                        title="Video Player"
                        src="//foa.23video.com/v.ihtml/player.html?token=6e3fc42b2b57143eb3cba9d092b8cc1a&source=embed&photo_id=78196588"
                        width="564"
                        height="352"
                        frameBorder="0"
                        scrolling="no"
                        allowFullScreen
                        allow="fullscreen"
                    />
                    {/* text needed */}
                    <p> {L_HTML('first_login_help_text')}</p>
                </div>
                <div className={`${classes.right}`}>
                    <h3 className={`${classes.headlines}`}>
                        {L_HTML('first_login_list_heading')}
                    </h3>
                    <ul className={`${classes.list}`}>
                        <li className={`${classes.listItem}`}>
                            <span className={`${classes.listItemSpan}`}>
                                <TipsIcon
                                    width="25px"
                                    color={colors.green500}
                                />
                            </span>
                            <p className={`${classes.listItemBold}`}>
                                {L_HTML('first_login_listitem_1_heading')}
                            </p>
                            <p>{L_HTML('first_login_listitem_1_text')}</p>
                        </li>
                        <li className={`${classes.listItem}`}>
                            <span className={`${classes.listItemSpan}`}>
                                <TipsIcon
                                    width="25px"
                                    color={colors.green500}
                                />
                            </span>
                            <p className={`${classes.listItemBold}`}>
                                {L_HTML('first_login_listitem_2_heading')}
                            </p>
                            <p>{L_HTML('first_login_listitem_2_text')}</p>
                        </li>
                    </ul>
                    <button
                        className={`${classes.primaryButton}`}
                        type="button"
                        onClick={() => {
                            closeInfo()
                            setClose()
                        }}
                    >
                        {L_HTML('first_login_button_text')}
                    </button>
                </div>
            </div>
        </div>,
        document.getElementById('root') as HTMLElement
    )
}

export { FirstLoginInfo }
